import type Phaser from "phaser";

export interface PhaserCanvasCountOptions {
  /** 테스트/호스트 앱 주입용 DPR. 생략하면 window.devicePixelRatio를 읽는다. */
  readonly devicePixelRatio?: number;
}

interface CanvasLike {
  readonly width?: number;
  readonly height?: number;
  readonly clientWidth?: number;
  readonly clientHeight?: number;
  getBoundingClientRect?: () => { width?: number; height?: number };
}

const finiteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const roundedPositive = (value: unknown): number | null =>
  finiteNumber(value) && value > 0 ? Math.round(value) : null;

const currentDevicePixelRatio = (): number | null => {
  const maybeWindow = (globalThis as unknown as { window?: { devicePixelRatio?: number } }).window;
  return finiteNumber(maybeWindow?.devicePixelRatio) && maybeWindow.devicePixelRatio > 0
    ? maybeWindow.devicePixelRatio
    : null;
};

const configResolution = (game: Phaser.Game): number | null => {
  const value = (game.config as unknown as { resolution?: unknown }).resolution;
  const parsed =
    typeof value === "string" && value.trim() !== "" ? Number.parseFloat(value) : value;
  return finiteNumber(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Phaser canvas backing store와 CSS 표시 크기를 카운트 형태로 변환한다.
 *
 * 모바일 WebView와 데스크톱 웹의 그래픽/성능 차이는 표시 객체 수만으로 설명되지 않는 경우가 많다.
 * 이 함수는 backing px, CSS px, DPR, Phaser resolution을 perf counts에 합쳐 같은 로그 포맷으로
 * 비교할 수 있게 한다.
 */
export function countPhaserCanvas(
  game: Phaser.Game,
  options: PhaserCanvasCountOptions = {},
): Record<string, number> {
  const canvas = (game as unknown as { canvas?: CanvasLike }).canvas;
  if (!canvas) return {};

  const counts: Record<string, number> = {};
  const canvasW = roundedPositive(canvas.width);
  const canvasH = roundedPositive(canvas.height);
  if (canvasW !== null) counts.canvasW = canvasW;
  if (canvasH !== null) counts.canvasH = canvasH;

  const rect = canvas.getBoundingClientRect?.();
  const cssW = roundedPositive(rect?.width) ?? roundedPositive(canvas.clientWidth);
  const cssH = roundedPositive(rect?.height) ?? roundedPositive(canvas.clientHeight);
  if (cssW !== null) counts.canvasCssW = cssW;
  if (cssH !== null) counts.canvasCssH = cssH;

  if (canvasW !== null && cssW !== null) {
    counts.canvasBackingScaleX100 = Math.round((canvasW / cssW) * 100);
  }
  if (canvasH !== null && cssH !== null) {
    counts.canvasBackingScaleY100 = Math.round((canvasH / cssH) * 100);
  }

  const dpr = options.devicePixelRatio ?? currentDevicePixelRatio();
  if (finiteNumber(dpr) && dpr > 0) counts.canvasDpr100 = Math.round(dpr * 100);

  const resolution = configResolution(game);
  if (resolution !== null) counts.canvasResolution100 = Math.round(resolution * 100);

  return counts;
}
